import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'

import List from 'components/Common/List'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { StyledModalImageThumbnail } from 'LuxKit/Modal/ModalImageThumbnail'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import GooglePlaceRating from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import RestoreCartModalFooter from 'tripPlanner/components/TripModal/TripItems/Common/RestoreCartModalFooter'
import AddressWithViewMapButton from 'tripPlanner/components/TripModal/TripItems/Summary/Common/AddressWithViewMapButton'
import ExperienceSummaryModalFooter from 'tripPlanner/components/TripModal/TripItems/Summary/Common/SummaryModalFooter/ExperienceSummaryModalFooter'
import ReadOnlyItemFooter from 'tripPlanner/components/TripModal/TripItems/Summary/Common/SummaryModalFooter/ReadOnlyItemFooter'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { useOrder } from 'tripPlanner/hooks/api/order'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { ExperienceItem } from 'tripPlanner/types/tripItem'
import { getOrderId, isLeBookmark, maybePlaceId } from 'tripPlanner/utils'
import { isBookableItem } from 'tripPlanner/utils/bookmark/restoreCart'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

interface Props {
  item: ExperienceItem
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 0 ${rem(12)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const BookingDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding-bottom: ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const Dates = styled.div`
  display: flex;
  justify-content: space-between;
`

const Tickets = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${rem(16)};
`

const Date = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const Duration = Date
const ImageElement = styled.img`
  overflow: hidden;
  object-fit: cover;
  object-position: center;
  width: 100%;
  height: ${rem(168)};

  ${mediaQueryUp.tablet} {
    height: ${rem(315)};
  }
`

function ExperienceItemSummary(props: Props) {
  const { item } = props
  const { startDate, startTime } = props.item
  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const canEdit = useCanEditTrip()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const isBookable = isLeBookmark(item) && isBookableItem(item)

  const orderId = getOrderId(item)

  const { data: order, isLoading: isOrderLoading } = useOrder(orderId)
  const experienceId = item.code
  const { data: experience, isLoading: isExperienceLoading } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  const isLoading = (!!orderId && isOrderLoading) || isExperienceLoading

  const { closeModal, goBack } = useModalCallbacks()

  const getOfferUrl = useCallback(() => {
    if (experience) {
      return getExperienceOfferPageURL(experience)
    }
  }, [experience])
  const offerUrl = getOfferUrl()

  const durationString = useMemo(() => {
    if (experience) {
      return getExperienceDurationString(experience, intl)
    }
  }, [experience, intl])
  const hasDuration = useMemo(
    () =>
      !!(experience && (experience.duration.from || experience.duration.to)),
    [experience],
  )
  const placeId = maybePlaceId(item.place)

  const viewAllDetailsLabel = intl.formatMessage({
    id: 'XAek3/OeSy',
    defaultMessage: 'View all details',
    description: 'Link text to view full experience offer details from trip item summary',
  })
  const bookingDetailsHeading = intl.formatMessage({
    id: 'hybDVUCat+',
    defaultMessage: 'Your Booking Details',
    description: 'Heading for the booking details section in experience trip item summary',
  })
  const totalPaidLabel = intl.formatMessage({
    id: 'IPKFdkzZpn',
    defaultMessage: 'Total Paid',
    description: 'Label for total amount paid in experience booking details',
  })
  const dateLabel = intl.formatMessage({
    id: 'Hkkix4KYi4',
    defaultMessage: 'Date',
    description: 'Label for the date field in experience trip item summary',
  })
  const durationLabel = intl.formatMessage({
    id: 'AGNMoL4cET',
    defaultMessage: 'Duration',
    description: 'Label for the duration field in experience trip item summary',
  })
  const pickUpLabel = intl.formatMessage({
    id: 'nSfFZdRqFJ',
    defaultMessage: 'Pick up',
    description: 'Label for the pick up location in experience trip item summary',
  })
  const reviewsLabel = intl.formatMessage({
    id: 'kXgqoTciRj',
    defaultMessage: 'Reviews',
    description: 'Label for the Google reviews section in experience trip item summary',
  })

  return (
    <>
      <ModalHeader
        title={item.name}
        subtitle={item.place.name}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        {experience?.images?.[0].url && (
          <StyledModalImageThumbnail>
            <ImageElement
              src={experience.images[0].url}
              alt={experience.images[0]?.title}
            />
          </StyledModalImageThumbnail>
        )}
        {!experience && item.place?.type === 'GEO' && (
          <StyledModalImageThumbnail>
            <ImageElement src={item.place.photoUrl} alt={item.place.name} />
          </StyledModalImageThumbnail>
        )}
        <ModalContent>
          {isLoading && <LoadingIndicator inline />}
          {!isLoading && (
            <VerticalSpacer gap={20}>
              <ItemTypeAndBookedLabel item={item} />
              <Header>
                {experience && (
                  <Typography align="start">
                    {experience.copy.highlights.length === 0 && (
                      <MarkdownRender
                        fontSize="large"
                        content={experience.copy.description}
                      />
                    )}
                    {experience.copy.highlights.length > 0 && (
                      <List>
                        {experience.copy.highlights.map((highlight) => (
                          <li key={highlight}>
                            <BodyText variant="large" as="span">
                              {highlight}
                            </BodyText>
                          </li>
                        ))}
                      </List>
                    )}
                  </Typography>
                )}
                {offerUrl && (
                  <TextLink
                    weight="bold"
                    to={offerUrl}
                    onClick={() =>
                      setImmersiveMode({
                        tripId: item.tripId,
                        sectionId: tripItemSelectionId(item.id),
                      })}
                  >
                    {viewAllDetailsLabel}
                  </TextLink>
                )}
              </Header>
              {order && (
                <BookingDetails>
                  <Heading variant="heading5">{bookingDetailsHeading}</Heading>
                  <div>
                    <BodyText variant="medium" weight="bold">
                      {totalPaidLabel}
                    </BodyText>
                    <BodyText variant="large">${order.total}</BodyText>
                  </div>
                  <Tickets>
                    {order.experienceItems.map((experienceItem, index) => (
                      <div key={experienceItem.id}>
                        <BodyText variant="medium" weight="bold">
                          {intl.formatMessage(
                            {
                              id: '7e7lTw8mx+',
                              defaultMessage: 'Ticket {ticketNumber}',
                              description: 'Label for a numbered ticket in experience booking details',
                            },
                            { ticketNumber: index + 1 },
                          )}
                        </BodyText>
                        <BodyText variant="large">
                          {experienceItem.ticket.name}
                        </BodyText>
                        <BodyText variant="large" colour="neutral-two">
                          {experienceItem.bookingNumber}
                        </BodyText>
                      </div>
                    ))}
                  </Tickets>
                </BookingDetails>
              )}
              <Dates>
                <Date>
                  <BodyText variant="medium" weight="bold">
                    {dateLabel}
                  </BodyText>
                  <BodyText variant="large">
                    {[
                      startDate?.format('ddd, DD MMMM YYYY'),
                      startTime?.format('h:mma'),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </BodyText>
                </Date>
                {hasDuration && (
                  <Duration>
                    <BodyText variant="medium" weight="bold">
                      {durationLabel}
                    </BodyText>
                    <BodyText variant="large">{durationString}</BodyText>
                  </Duration>
                )}
              </Dates>
              {experience && (
                <AddressWithViewMapButton
                  item={item}
                  addressLabel={pickUpLabel}
                  address={experience.location.name}
                />
              )}
              {placeId && (
                <GooglePlaceRating size="M" placeId={placeId} label={reviewsLabel} />
              )}
            </VerticalSpacer>
          )}
        </ModalContent>
      </ModalBody>

      {canEdit && (
        <>
          {isBookable && <RestoreCartModalFooter item={item} />}
          {!isBookable && <ExperienceSummaryModalFooter item={item} />}
        </>
      )}
      {!canEdit && <ReadOnlyItemFooter item={item} />}
    </>
  )
}

export default ExperienceItemSummary
